/**
 * Copying nodes and pasting them, in every view (pure: no DOM, no core). What is copied is
 * text for the system's clipboard, so nodes go from one view, board, page or project to
 * another: the nodes with what they hold (kind, categories, note, data, files, look, pins), the
 * links between them, and where each one sits in every view (the nodal view's point, the
 * diagram's box, the board's rectangle), and the groups copied whole (a board's frames, with
 * the frames and groups inside them). A paste makes new nodes and groups with ids of their own,
 * linked and nested as they were, put where the paste is, the way they were laid out.
 */
import type { BoardPlace } from "@protocol/BoardPlace";
import type { Graph } from "@protocol/Graph";
import type { GraphEdit } from "@protocol/GraphEdit";
import type { Link } from "@protocol/Link";
import type { NewNode } from "../client/actions";

/** A point of a view (world units). */
export interface Point {
  x: number;
  y: number;
}

/** One copied node and its places. */
export interface CopiedNode {
  /** Its id where it was copied (what the copied links name). */
  ref: string;
  node: NewNode;
  /** Its point in the nodal view. */
  nodal?: Point;
  /** Its box's corner in the class diagram. */
  diagram?: [number, number];
  /** Its rectangle on the board. */
  board?: BoardPlace;
  /** The `ref` of the copied group it is in (none: it was in no copied group). */
  group?: string;
}

/** One group copied whole (a frame, with what it holds). */
export interface CopiedGroup {
  /** Its id where it was copied (what the copied nodes and groups name). */
  ref: string;
  name: string;
  note?: string;
  /** The `ref` of the copied group it is in (none: the top of what was copied). */
  parent?: string;
  /** Its rectangle on the board (a frame). */
  board?: BoardPlace;
  /** Its look. */
  style?: Record<string, string>;
  /** Whether it was folded. */
  collapsed?: boolean;
}

/** What the clipboard holds (as JSON text). */
export interface Copied {
  knodal: "nodes";
  items: CopiedNode[];
  /** The links between the copied nodes (`from`, `to`: their `ref`s). */
  links: Link[];
  /** The groups copied whole, each after the one it is in. */
  groups?: CopiedGroup[];
}

/** Where each node sits, by view, when it is copied. */
export interface CopyPlaces {
  nodal(id: string): Point | undefined;
  diagram(id: string): [number, number] | undefined;
  board(id: string): BoardPlace | undefined;
}

/** The views a paste lands in. */
export type PasteView = "nodal" | "diagram" | "board";

/** A group a paste adds (its `parent`: a new group, or `null` for the top of the paste). */
export interface PastedGroup {
  id: string;
  name: string;
  note: string;
  parent: string | null;
  board?: BoardPlace;
  style?: Record<string, string>;
  collapsed?: boolean;
}

/** What a paste adds. */
export interface Pasted {
  /** The groups first, each after the one it is in (see {@link groupEdits}). */
  groups: PastedGroup[];
  nodes: NewNode[];
  links: Link[];
  /** The nodal view's starting points of the new nodes, by name (`scene.hint`). */
  hints: { name: string; x: number; y: number }[];
}

/** How far a paste with no point moves the copies from the originals. */
export const PASTE_OFFSET = 24;

/** The groups `ids` of `graph` and every group inside them, each after the one it is in. */
function groupsWithin(graph: Graph, ids: readonly string[]): Graph["groups"] {
  const inside = new Set(ids.filter((id) => graph.groups.some((g) => g.id === id)));
  for (let grew = true; grew; ) {
    grew = false;
    for (const g of graph.groups) if (g.parent && inside.has(g.parent) && !inside.has(g.id)) inside.add(g.id), (grew = true);
  }
  const ordered: Graph["groups"] = [];
  const placed = new Set<string>();
  // Parents first: go round until every group whose parent is placed (or not copied) is.
  for (let grew = true; grew; ) {
    grew = false;
    for (const g of graph.groups) {
      if (!inside.has(g.id) || placed.has(g.id) || (g.parent && inside.has(g.parent) && !placed.has(g.parent))) continue;
      ordered.push(g);
      placed.add(g.id);
      grew = true;
    }
  }
  return ordered;
}

/**
 * The clipboard text holding the nodes `ids` of `graph` and the groups `groups` whole (with the
 * groups and nodes inside them); `null`: none of them is there.
 */
export function copyNodes(graph: Graph, ids: readonly string[], places: CopyPlaces, groups: readonly string[] = []): string | null {
  const whole = groupsWithin(graph, groups);
  const copiedGroups = new Set(whole.map((g) => g.id));
  const wanted = new Set([...ids, ...graph.nodes.filter((n) => n.group && copiedGroups.has(n.group)).map((n) => n.id)]);
  const items = graph.nodes.flatMap((n): CopiedNode[] => {
    if (!wanted.has(n.id)) return [];
    const node: NewNode = {
      name: n.name,
      kind: n.kind,
      categories: [...n.categories],
      ...(n.note ? { note: n.note } : {}),
      ...(n.data && Object.keys(n.data).length ? { data: { ...n.data } as Record<string, string> } : {}),
      ...(n.attachments?.length ? { attachments: [...n.attachments] } : {}),
      ...(n.style && Object.keys(n.style).length ? { style: { ...n.style } as Record<string, string> } : {}),
      ...(n.pins?.length ? { pins: n.pins.map((p) => ({ ...p })) } : {}),
    };
    const nodal = places.nodal(n.id);
    const diagram = places.diagram(n.id) ?? n.diagram ?? undefined;
    const board = places.board(n.id) ?? n.board ?? undefined;
    const group = n.group && copiedGroups.has(n.group) ? n.group : undefined;
    return [{ ref: n.id, node, ...(nodal ? { nodal: { x: nodal.x, y: nodal.y } } : {}), ...(diagram ? { diagram: [diagram[0], diagram[1]] as [number, number] } : {}), ...(board ? { board: { ...board } } : {}), ...(group ? { group } : {}) }];
  });
  if (!items.length && !whole.length) return null;
  const links = graph.links.filter((l) => wanted.has(l.from) && wanted.has(l.to)).map((l) => ({ ...l }));
  const copied: Copied = { knodal: "nodes", items, links };
  if (whole.length)
    copied.groups = whole.map((g): CopiedGroup => {
      const board = places.board(g.id) ?? g.board ?? undefined;
      return {
        ref: g.id,
        name: g.name,
        ...(g.note ? { note: g.note } : {}),
        ...(g.parent && copiedGroups.has(g.parent) ? { parent: g.parent } : {}),
        ...(board ? { board: { ...board } } : {}),
        ...(g.style && Object.keys(g.style).length ? { style: { ...g.style } as Record<string, string> } : {}),
        ...(g.collapsed ? { collapsed: true } : {}),
      };
    });
  return JSON.stringify(copied);
}

/**
 * The nodes in clipboard text `text`, or `null` when it holds none. The board's older copies
 * (`{ knodal: "board", items }`, nodes with their rectangles) are read too.
 */
export function readCopied(text: string): Copied | null {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    return null;
  }
  if (!data || typeof data !== "object") return null;
  const d = data as { knodal?: unknown; items?: unknown; links?: unknown; groups?: unknown };
  const groups = Array.isArray(d.groups) ? (d.groups as CopiedGroup[]).filter((g) => g && typeof g.ref === "string" && typeof g.name === "string") : [];
  if (!Array.isArray(d.items) || (!d.items.length && !groups.length)) return null;
  if (d.knodal === "board") {
    const items = (d.items as NewNode[]).map((node, i): CopiedNode => {
      const { board, ...rest } = node;
      return { ref: `copied-${i}`, node: rest, ...(board ? { board } : {}) };
    });
    return { knodal: "nodes", items, links: [] };
  }
  if (d.knodal !== "nodes") return null;
  const items = (d.items as CopiedNode[]).filter((i) => i && typeof i.ref === "string" && i.node && typeof i.node.name === "string" && typeof i.node.kind === "string");
  if (!items.length && !groups.length) return null;
  return { knodal: "nodes", items, links: Array.isArray(d.links) ? (d.links as Link[]) : [], ...(groups.length ? { groups } : {}) };
}

/** A name as an id, the way the core makes one (`unique_id` in `mod-graph`): lower case, words joined by `-`. */
export function slug(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  return base || "node";
}

/** Ids for nodes named `names`, none used by `graph` nor by each other (`-2`, `-3`... added). */
export function freshIds(graph: Graph, names: readonly string[]): string[] {
  const taken = new Set(graph.nodes.map((n) => n.id));
  return names.map((name) => {
    const base = slug(name);
    let id = base;
    for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
    taken.add(id);
    return id;
  });
}

/** Group ids for groups named `names`, used by no group nor node of `graph`, nor by `also` or each other. */
export function freshGroupIds(graph: Graph, names: readonly string[], also: readonly string[] = []): string[] {
  const taken = new Set([...graph.groups.map((g) => g.id), ...graph.nodes.map((n) => n.id), ...also]);
  return names.map((name) => {
    const base = slug(name) === "node" ? "group" : slug(name);
    let id = base;
    for (let n = 2; taken.has(id); n++) id = `${base}-${n}`;
    taken.add(id);
    return id;
  });
}

/** The middle of `points` (`null` for none). */
function middle(points: Point[]): Point | null {
  if (!points.length) return null;
  return { x: points.reduce((s, p) => s + p.x, 0) / points.length, y: points.reduce((s, p) => s + p.y, 0) / points.length };
}

/**
 * What pasting `copied` into `graph` adds, in view `view`, around `at` (a point of that view;
 * `null`: beside the originals). The nodes keep how they were laid out in that view; their
 * places in the other views are left to those views.
 */
export function pasteOf(graph: Graph, copied: Copied, view: PasteView, at: Point | null): Pasted {
  const ids = freshIds(
    graph,
    copied.items.map((i) => i.node.name),
  );
  const byRef = new Map(copied.items.map((item, i) => [item.ref, ids[i]!]));
  const copiedGroups = copied.groups ?? [];
  const groupIds = freshGroupIds(
    graph,
    copiedGroups.map((g) => g.name),
    ids,
  );
  const groupByRef = new Map(copiedGroups.map((g, i) => [g.ref, groupIds[i]!]));
  const centreOf = (b: BoardPlace) => ({ x: b.x + b.w / 2, y: b.y + b.h / 2 });
  const placeOf = (item: CopiedNode): Point | undefined =>
    view === "nodal" ? item.nodal : view === "diagram" ? (item.diagram ? { x: item.diagram[0], y: item.diagram[1] } : undefined) : item.board ? centreOf(item.board) : undefined;
  // On the board, the copied frames count too (an empty frame is pasted where the pointer is).
  const frames = view === "board" ? copiedGroups.flatMap((g) => (g.board ? [centreOf(g.board)] : [])) : [];
  const centre = middle([...copied.items.flatMap((i) => placeOf(i) ?? []), ...frames]);
  const [dx, dy] = at && centre ? [at.x - centre.x, at.y - centre.y] : [PASTE_OFFSET, PASTE_OFFSET];
  const hints: Pasted["hints"] = [];
  const nodes = copied.items.map((item, i): NewNode => {
    const node: NewNode = { ...item.node, id: ids[i]!, categories: [...(item.node.categories ?? [])] };
    delete node.group;
    const group = item.group && groupByRef.get(item.group);
    if (group) node.group = group;
    delete node.board;
    delete node.diagram;
    if (view === "diagram" && item.diagram) node.diagram = [Math.round(item.diagram[0] + dx), Math.round(item.diagram[1] + dy)];
    if (view === "board" && item.board) node.board = { ...item.board, x: Math.round(item.board.x + dx), y: Math.round(item.board.y + dy) };
    if (view === "nodal" && item.nodal) hints.push({ name: node.name, x: item.nodal.x + dx, y: item.nodal.y + dy });
    return node;
  });
  const links = copied.links.flatMap((l): Link[] => {
    const from = byRef.get(l.from);
    const to = byRef.get(l.to);
    return from && to ? [{ ...l, from, to }] : [];
  });
  const groups = copiedGroups.map((g, i): PastedGroup => ({
    id: groupIds[i]!,
    name: g.name,
    note: g.note ?? "",
    parent: (g.parent && groupByRef.get(g.parent)) || null,
    ...(view === "board" && g.board ? { board: { ...g.board, x: Math.round(g.board.x + dx), y: Math.round(g.board.y + dy) } } : {}),
    ...(g.style ? { style: { ...g.style } } : {}),
    ...(g.collapsed ? { collapsed: true } : {}),
  }));
  return { groups, nodes, links, hints };
}

/** The edits making `groups` (empty, each after the one it is in), to go before the pasted nodes. */
export function groupEdits(groups: readonly PastedGroup[]): GraphEdit[] {
  return groups.flatMap((g): GraphEdit[] => {
    const patch = {
      ...(g.board ? { board: [Math.round(g.board.x), Math.round(g.board.y), Math.round(g.board.w), Math.round(g.board.h), g.board.rotate ?? 0, g.board.z ?? 0] } : {}),
      ...(g.style && Object.keys(g.style).length ? { style: g.style } : {}),
      ...(g.collapsed ? { collapsed: true } : {}),
    };
    return [{ edit: "add_group", id: g.id, name: g.name, parent: g.parent, members: [], note: g.note }, ...(Object.keys(patch).length ? [{ edit: "update_group", id: g.id, patch } satisfies GraphEdit] : [])];
  });
}
